// mui
import { Grid, Typography } from '@mui/material';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// little-state-machine
import { useStateMachine } from 'little-state-machine';
// component
import {
  FieldCondition,
  FormProvider,
  RHFSelect,
  RHFTextField,
} from '../../../../components/hook-form';
import FormSectionControls from '../../FormSectionControls';
// hooks
import useFormStateUpdateOnChange from '../../../../hooks/useFormStateUpdateOnChange';
// services
import formValidationSchema from '../../../../services/validation/formValidationSchema';

// -------------------------------------------------------

const Form = () => {
  // state
  const { state } = useStateMachine();

  // form
  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(formValidationSchema),
    defaultValues: state.form,
  });

  const { watch, setValue, getValues, trigger } = methods;

  useFormStateUpdateOnChange(watch);

  const handleEntityTypeChange = (event) => {
    setValue('insured.entityType', event.target.value);
    if (event.target.value !== 'Other') {
      setValue('insured.otherInfo', null);
    }
  };

  const handleStreetAddressFieldBlur = (event) => {
    setValue('insured.streetAddress', event.target.value);
    updateMailingAddress();
  };

  const handleCityFieldBlur = (event) => {
    setValue('insured.city', event.target.value);
    updateMailingAddress();
  };

  const handleStateFieldBlur = async (event) => {
    setValue('insured.state', event.target.value);
    await trigger('insured.state');
    updateMailingAddress();
  };

  const handleZipFieldBlur = (event) => {
    setValue('insured.zip', event.target.value);
    updateMailingAddress();
  };

  const updateMailingAddress = () => {
    const insuredAddress = getValues('insured.streetAddress');
    const insuredCity = getValues('insured.city');
    const insuredState = getValues('insured.state');
    const insuredZip = getValues('insured.zip');
      const mailingAddress = [
        insuredAddress,
        insuredCity,
        [insuredState, insuredZip].filter(x => x).join(' '),
      ].filter(x => x).join(', ');
      methods.setValue('policyOwnershipAndFraudStatement.mailingAddress', mailingAddress);
  }

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RHFTextField name={'insured.name'}label={'Name'} />
        </Grid>
        <Grid item xs={12}>
          <RHFTextField
            name={'insured.streetAddress'} 
            label={'Street Address'}
            onBlur={handleStreetAddressFieldBlur}/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <RHFTextField
            name={'insured.city'} 
            label={'City'}
            onBlur={handleCityFieldBlur}/>
        </Grid>
        <Grid item xs={12} sm={3}>
          <RHFTextField
            name={'insured.state'} 
            label={'State'}
            onBlur={handleStateFieldBlur}/>
        </Grid>
        <Grid item xs={12} sm={3}>
          <RHFTextField
            name={'insured.zip'} 
            label={'Zip'}
            onBlur={handleZipFieldBlur}/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <RHFTextField name={'insured.fein'} label={'FEIN'} />
        </Grid>
        <Grid item container xs={12} sm={6}>
          <RHFSelect
            name={'insured.entityType'}
            label={'Entity Type'}
            options={['Sole Proprietor', 'Partnership', 'Corporation', 'Subchapter “S” Corp', 'LLC', 'Joint Venture', 'Trust', 'Unincorporated Association', 'Other']}
            onChange={handleEntityTypeChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <RHFTextField name={'insured.yearsInBusiness'} label={'Years in business'} type="number" />
        </Grid>
        <FieldCondition fieldName="insured.entityType" condition={(val) => val === 'Other'}>
          <Grid item xs={12} sm={6}>
            <RHFTextField name={'insured.otherInfo'} label={'Other info'} />
          </Grid>
        </FieldCondition>
        <Grid item xs={12}>
          <Typography variant={'subtitle1'}>Inspection Contact</Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.inspectionContact.name'} label={'Name'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.inspectionContact.phone'} label={'Office or Mobile Phone #'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.inspectionContact.email'} label={'E-Mail'} />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant={'subtitle1'}>Accounting Contact</Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.accountingContact.name'} label={'Name'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.accountingContact.phone'} label={'Office or Mobile Phone #'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.accountingContact.email'} label={'E-Mail'} />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant={'subtitle1'}>Claims Contact</Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.claimsContact.name'} label={'Name'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.claimsContact.phone'} label={'Office or Mobile Phone #'} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <RHFTextField name={'insured.claimsContact.email'} label={'E-Mail'} />
        </Grid>
      </Grid>

      <FormSectionControls step={'insured'} />
    </FormProvider>
  );
};

export default Form;
